import React from 'react';
import { useLanguage } from '@/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, FileText, Phone } from 'lucide-react';

const Hero = () => {
  const { t } = useLanguage();

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/2654ee85-ac0d-4b04-a252-c1374ec6c4f8/images/f3fd0a2bb39309b8f7a114078be266c5b554619a04cbf1313c8d340fb174b4dd.png"
          alt="Medical professionals"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A1628]/75" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/60 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#C5A55A] text-sm font-mono tracking-[0.3em] uppercase mb-6"
            data-testid="hero-overline"
          >
            {t('hero.overline')}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-none text-white mb-8"
            data-testid="hero-title"
          >
            {t('hero.title')}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-slate-300 text-base md:text-lg leading-relaxed mb-12 max-w-2xl"
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex flex-wrap gap-4"
          >
            <a
              href="https://medecinesupply.com/wp-content/uploads/2023/07/product_catalogue3.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[#C5A55A] text-[#0A1628] font-semibold px-8 py-4 uppercase tracking-wider transition-all duration-300 shadow-[0_4px_14px_0_rgba(197,165,90,0.39)] hover:shadow-[0_6px_20px_rgba(197,165,90,0.23)] hover:-translate-y-1 flex items-center gap-3"
              data-testid="hero-shop-now-btn"
            >
              {t('hero.shopNow')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>

            <button
              onClick={() => scrollTo('#contact')}
              className="group bg-transparent border border-white/30 text-white font-medium px-8 py-4 hover:bg-white/10 transition-all duration-300 flex items-center gap-3 tracking-wider uppercase"
              data-testid="hero-quote-btn"
            >
              <FileText className="w-4 h-4" />
              {t('hero.requestQuote')}
            </button>

            <button
              onClick={() => scrollTo('#contact')}
              className="group bg-transparent border border-white/30 text-white font-medium px-8 py-4 hover:bg-white/10 transition-all duration-300 flex items-center gap-3 tracking-wider uppercase"
              data-testid="hero-contact-btn"
            >
              <Phone className="w-4 h-4" />
              {t('hero.contactSales')}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-6"
          >
            <a
              href="https://medecinesupply.com/wp-content/uploads/2023/07/product_catalogue3.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#C5A55A] hover:text-[#D7C085] text-sm font-medium transition-colors underline underline-offset-4"
              data-testid="hero-catalog-pdf-link"
            >
              <FileText className="w-4 h-4" />
              {t('hero.catalogPdf')}
            </a>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
};

export default Hero;
